import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase";
import { ref as dbRef, push, get, update } from "firebase/database";
import { uploadImage } from "../utils/uploadImage";
import { Camera, AlertCircle, Flame, ArrowLeft, Plus, X } from "lucide-react";

const CATEGORIES = [
  "Books", "Electronics", "Cycles & Vehicles", "Room Essentials",
  "Clothing & Uniforms", "Sports & Fitness", "Musical Instruments",
  "Study Tools", "Furniture", "Kitchen Items", "Art & Craft", "Other"
];

const CONDITIONS = ["Like New", "Good", "Fair", "Used"];
const MAX_IMAGES = 5;

export default function Sell() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get("edit");
  const fileInputRef = useRef(null);

  const [form, setForm] = useState({
    title: "",
    description: "",
    price: "",
    category: "",
    condition: "",
    whatsapp: userProfile?.phone || "",
  });
  const [isUrgent, setIsUrgent] = useState(false);
  const [isBundle, setIsBundle] = useState(false);
  const [bundleItems, setBundleItems] = useState("");
  const [scanning, setScanning] = useState(false);
  const [scanStatus, setScanStatus] = useState("");
  const scannerRef = useRef(null);
  const [dupWarning, setDupWarning] = useState(false);
  const [priceSuggestion, setPriceSuggestion] = useState(null);
  // Each item: { type: 'existing', url } | { type: 'new', file, preview }
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [error, setError] = useState("");
  const [agreedToGuidelines, setAgreedToGuidelines] = useState(false);
  // Image picker bottom sheet
  const [showImagePicker, setShowImagePicker] = useState(false);
  // Rental feature
  const [isRental, setIsRental] = useState(false);
  const [rentalData, setRentalData] = useState({
    pricePerDayEnabled: false,   pricePerDay: "",
    pricePerWeekEnabled: false,  pricePerWeek: "",
    pricePerMonthEnabled: false, pricePerMonth: "",
    minDuration: "1",
    maxDuration: "30",
    deposit: "",
    availableFrom: "",
    availableTo: "",
    deliveryType: "pickup",
    deliveryCharge: "",
    cancellationPolicy: "free",
    lateFeePerDay: "",
  });
  function setRD(key, val) { setRentalData(prev => ({ ...prev, [key]: val })); }
  // Track original price for price-drop alerts on edit
  const originalPriceRef = useRef(null);

  // Load existing listing data when editing
  useEffect(() => {
    if (!editId) return;
    setLoadingEdit(true);
    get(dbRef(db, `listings/${editId}`)).then(snap => {
      if (snap.exists()) {
        const d = snap.val();
        setForm({
          title: d.title || "",
          description: d.description || "",
          price: d.price != null ? String(d.price) : "",
          category: d.category || "",
          condition: d.condition || "",
          whatsapp: d.whatsapp || userProfile?.phone || "",
        });
        setIsUrgent(!!d.urgent);
        setIsBundle(!!d.isBundle);
        setBundleItems(d.bundleItems || "");
        setIsRental(!!d.isRental);
        if (d.rental) {
          setRentalData({
            pricePerDayEnabled: d.rental.pricePerDay != null,
            pricePerDay: d.rental.pricePerDay ?? "",
            pricePerWeekEnabled: d.rental.pricePerWeek != null,
            pricePerWeek: d.rental.pricePerWeek ?? "",
            pricePerMonthEnabled: d.rental.pricePerMonth != null,
            pricePerMonth: d.rental.pricePerMonth ?? "",
            minDuration: String(d.rental.minDuration ?? 1),
            maxDuration: String(d.rental.maxDuration ?? 30),
            deposit: d.rental.deposit != null ? String(d.rental.deposit) : "",
            availableFrom: d.rental.availableFrom || "",
            availableTo: d.rental.availableTo || "",
            deliveryType: d.rental.deliveryType || "pickup",
            deliveryCharge: d.rental.deliveryCharge != null ? String(d.rental.deliveryCharge) : "",
            cancellationPolicy: d.rental.cancellationPolicy || "free",
            lateFeePerDay: d.rental.lateFeePerDay != null ? String(d.rental.lateFeePerDay) : "",
          });
        } else if (d.rentalPeriod) {
          // backward compat: old-style rental with single period
          setRentalData(prev => ({
            ...prev,
            pricePerDayEnabled: d.rentalPeriod === "per_day",
            pricePerDay: d.rentalPeriod === "per_day" ? String(d.price || "") : "",
            pricePerWeekEnabled: d.rentalPeriod === "per_week",
            pricePerWeek: d.rentalPeriod === "per_week" ? String(d.price || "") : "",
            pricePerMonthEnabled: d.rentalPeriod === "per_month",
            pricePerMonth: d.rentalPeriod === "per_month" ? String(d.price || "") : "",
          }));
        }
        // Store original price for price-drop alert detection
        originalPriceRef.current = d.price != null ? d.price : 0;
        // Load existing images
        const existing = d.images?.length
          ? d.images.map(url => ({ type: "existing", url }))
          : d.imageUrl
            ? [{ type: "existing", url: d.imageUrl }]
            : [];
        setImages(existing);
      }
      setLoadingEdit(false);
    }).catch(() => setLoadingEdit(false));
  }, [editId]); // eslint-disable-line

  useEffect(() => {
    if (editId || form.title.trim().length < 8) { setDupWarning(false); return; }
    const timer = setTimeout(async () => {
      try {
        const snap = await get(dbRef(db, "listings"));
        if (snap.exists()) {
          const titleLower = form.title.trim().toLowerCase();
          const dup = Object.values(snap.val()).find(l =>
            l.sellerId === currentUser?.uid &&
            l.status === "active" &&
            l.title?.toLowerCase().includes(titleLower.slice(0, 12))
          );
          setDupWarning(!!dup);
        }
      } catch (e) {}
    }, 600);
    return () => clearTimeout(timer);
  }, [form.title, editId, currentUser]); // eslint-disable-line

  useEffect(() => {
    if (!form.category) { setPriceSuggestion(null); return; }
    async function fetchPriceRange() {
      try {
        const snap = await get(dbRef(db, "listings"));
        if (snap.exists()) {
          const similar = Object.values(snap.val())
            .filter(l => l.category === form.category && l.status === "sold" && l.price > 0);
          if (similar.length >= 3) {
            const prices = similar.map(l => l.price).sort((a, b) => a - b);
            const low = prices[Math.floor(prices.length * 0.25)];
            const high = prices[Math.floor(prices.length * 0.75)];
            setPriceSuggestion({ low, high, count: similar.length });
          } else {
            setPriceSuggestion(null);
          }
        }
      } catch (e) {}
    }
    fetchPriceRange();
  }, [form.category]); // eslint-disable-line

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }

  function handleImageFiles(files) {
    const remaining = MAX_IMAGES - images.length;
    if (remaining <= 0) return;
    const toAdd = Array.from(files).slice(0, remaining);
    const invalid = toAdd.find(f => f.size > 5 * 1024 * 1024);
    if (invalid) { setError("Each image must be under 5MB."); return; }
    const newImgs = toAdd.map(file => ({ type: "new", file, preview: URL.createObjectURL(file) }));
    setImages(prev => [...prev, ...newImgs]);
  }

  function removeImage(idx) {
    setImages(prev => prev.filter((_, i) => i !== idx));
  }

  async function scanBarcode() {
    if (!("BarcodeDetector" in window)) {
      alert("Barcode scanning is not supported on this browser. Try Chrome on Android.");
      return;
    }
    setScanStatus("Scanning... Point camera at barcode");
    setScanning(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      const video = document.createElement("video");
      video.srcObject = stream;
      video.play();
      const detector = new window.BarcodeDetector({ formats: ["ean_13", "ean_8", "isbn"] });
      let found = false;
      const check = async () => {
        if (found) return;
        const codes = await detector.detect(video);
        if (codes.length > 0) {
          found = true;
          stream.getTracks().forEach(t => t.stop());
          setScanning(false);
          const isbn = codes[0].rawValue;
          setScanStatus("Looking up book details...");
          try {
            const res = await fetch(`https://openlibrary.org/api/books?bibkeys=ISBN:${isbn}&format=json&jscmd=data`);
            const data = await res.json();
            const book = data[`ISBN:${isbn}`];
            if (book) {
              setForm(prev => ({
                ...prev,
                title: book.title || prev.title,
                description: `Author: ${book.authors?.[0]?.name || "Unknown"}\nPublisher: ${book.publishers?.[0]?.name || "Unknown"}\nISBN: ${isbn}\n${prev.description}`.trim(),
                category: "Books",
              }));
              setScanStatus("✅ Book found! Details filled in.");
            } else {
              setForm(prev => ({ ...prev, title: prev.title || `Book (ISBN: ${isbn})`, category: "Books" }));
              setScanStatus("✅ ISBN scanned. Book details not found — please fill manually.");
            }
          } catch (e) {
            setForm(prev => ({ ...prev, title: prev.title || `Book (ISBN: ${isbn})`, category: "Books" }));
            setScanStatus("✅ ISBN scanned. Fill remaining details manually.");
          }
          setTimeout(() => setScanStatus(""), 4000);
        } else {
          requestAnimationFrame(check);
        }
      };
      video.onplay = () => requestAnimationFrame(check);
      setTimeout(() => {
        if (!found) {
          stream.getTracks().forEach(t => t.stop());
          setScanning(false);
          setScanStatus("No barcode detected. Try again in better lighting.");
          setTimeout(() => setScanStatus(""), 3000);
        }
      }, 10000);
    } catch (e) {
      setScanning(false);
      setScanStatus("");
      alert("Could not access camera. Please allow camera permission.");
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    if (!form.title.trim() || form.title.trim().length < 5) return setError("Title must be at least 5 characters.");
    if (!editId && images.length === 0) return setError("Please add at least one photo. Listings without photos rarely sell.");
    if (!form.category) return setError("Please select a category.");
    if (!form.condition) return setError("Please select a condition.");
    if (!isRental && form.price !== "" && Number(form.price) < 0) return setError("Price cannot be negative.");
    if (isRental) {
      const hasPx = rentalData.pricePerDayEnabled || rentalData.pricePerWeekEnabled || rentalData.pricePerMonthEnabled;
      if (!hasPx) return setError("Please set at least one rental price (per day, week, or month).");
      if (rentalData.pricePerDayEnabled && (!rentalData.pricePerDay || Number(rentalData.pricePerDay) <= 0)) return setError("Per-day price must be greater than 0.");
      if (rentalData.pricePerWeekEnabled && (!rentalData.pricePerWeek || Number(rentalData.pricePerWeek) <= 0)) return setError("Per-week price must be greater than 0.");
      if (rentalData.pricePerMonthEnabled && (!rentalData.pricePerMonth || Number(rentalData.pricePerMonth) <= 0)) return setError("Per-month price must be greater than 0.");
      if (Number(rentalData.minDuration) < 1) return setError("Minimum duration must be at least 1 day.");
      if (Number(rentalData.maxDuration) < Number(rentalData.minDuration)) return setError("Max duration must be ≥ minimum duration.");
      if (rentalData.availableFrom && rentalData.availableTo && new Date(rentalData.availableTo) <= new Date(rentalData.availableFrom)) return setError("Availability end date must be after start date.");
      if (rentalData.deliveryType !== "pickup" && rentalData.deliveryCharge !== "" && Number(rentalData.deliveryCharge) < 0) return setError("Delivery charge cannot be negative.");
    }
    if (!form.whatsapp.trim()) return setError("Please enter your WhatsApp number.");
    if (!/^[6-9]\d{9}$/.test(form.whatsapp.replace(/\s/g, ""))) {
      return setError("Enter a valid 10-digit WhatsApp number.");
    }

    if (userProfile?.suspended) return setError("Your account has been suspended. Contact support@campusswap.in.");

    // Build rental payload
    const rentalPayload = isRental ? {
      pricePerDay: rentalData.pricePerDayEnabled ? (Number(rentalData.pricePerDay) || null) : null,
      pricePerWeek: rentalData.pricePerWeekEnabled ? (Number(rentalData.pricePerWeek) || null) : null,
      pricePerMonth: rentalData.pricePerMonthEnabled ? (Number(rentalData.pricePerMonth) || null) : null,
      minDuration: Number(rentalData.minDuration) || 1,
      maxDuration: Number(rentalData.maxDuration) || 30,
      deposit: Number(rentalData.deposit) || 0,
      availableFrom: rentalData.availableFrom || null,
      availableTo: rentalData.availableTo || null,
      deliveryType: rentalData.deliveryType,
      deliveryCharge: rentalData.deliveryType !== "pickup" ? (Number(rentalData.deliveryCharge) || 0) : 0,
      cancellationPolicy: rentalData.cancellationPolicy,
      lateFeePerDay: Number(rentalData.lateFeePerDay) || 0,
    } : null;
    // Rental listings auto-compute display price from lowest per-day equivalent
    const rentalDisplayPrice = isRental ? (
      rentalData.pricePerDayEnabled ? (Number(rentalData.pricePerDay) || 0) :
      rentalData.pricePerWeekEnabled ? Math.floor(Number(rentalData.pricePerWeek) / 7) :
      rentalData.pricePerMonthEnabled ? Math.floor(Number(rentalData.pricePerMonth) / 30) : 0
    ) : null;
    // Legacy period field for backward compat
    const legacyPeriod = isRental ? (
      rentalData.pricePerDayEnabled ? "per_day" :
      rentalData.pricePerWeekEnabled ? "per_week" : "per_month"
    ) : null;

    setLoading(true);
    try {
      // Upload any new images
      const uploadedUrls = await Promise.all(
        images.map(img =>
          img.type === "existing" ? Promise.resolve(img.url) : uploadImage(img.file)
        )
      );

      const imageUrl = uploadedUrls[0] || null;

      if (editId) {
        const newPrice = isRental ? rentalDisplayPrice : (Number(form.price) || 0);
        await update(dbRef(db, `listings/${editId}`), {
          title: form.title.trim(),
          description: form.description.trim(),
          price: newPrice,
          category: form.category,
          condition: form.condition,
          whatsapp: form.whatsapp.trim(),
          imageUrl,
          images: uploadedUrls,
          urgent: isUrgent,
          isBundle: isBundle,
          bundleItems: isBundle ? bundleItems.trim() : "",
          isRental: isRental,
          rentalPeriod: legacyPeriod,
          rental: rentalPayload,
          sellerPhotoURL: userProfile?.photoURL || null,
        });
        // Send price-drop alerts if price decreased
        const oldPrice = originalPriceRef.current;
        if (oldPrice !== null && newPrice < oldPrice && newPrice > 0) {
          try {
            const alertsSnap = await get(dbRef(db, `priceAlerts/${editId}`));
            if (alertsSnap.exists()) {
              const alertUsers = Object.keys(alertsSnap.val()).filter(uid => uid !== currentUser.uid);
              for (const uid of alertUsers) {
                await push(dbRef(db, `notifications/${uid}`), {
                  type: "price_drop",
                  message: `📉 Price dropped on "${form.title.trim()}": ₹${oldPrice} → ₹${newPrice}`,
                  listingId: editId,
                  createdAt: Date.now(),
                  read: false,
                });
              }
            }
          } catch (e) { /* non-critical */ }
        }
        navigate(`/listing/${editId}`);
      } else {
        const newRef = await push(dbRef(db, "listings"), {
          title: form.title.trim(),
          description: form.description.trim(),
          price: isRental ? rentalDisplayPrice : (Number(form.price) || 0),
          category: form.category,
          condition: form.condition,
          whatsapp: form.whatsapp.trim(),
          imageUrl,
          images: uploadedUrls,
          status: "active",
          urgent: isUrgent,
          isBundle: isBundle,
          bundleItems: isBundle ? bundleItems.trim() : "",
          isRental: isRental,
          rentalPeriod: legacyPeriod,
          rental: rentalPayload,
          sellerPhotoURL: userProfile?.photoURL || null,
          sellerId: currentUser.uid,
          sellerName: userProfile?.name || currentUser.displayName || "Anonymous",
          college: userProfile?.college || "",
          sellerVerified: userProfile?.verified || false,
          createdAt: new Date().toISOString(),
        });

        // Notify alert watchers
        try {
          const alertsSnap = await get(dbRef(db, "alerts"));
          if (alertsSnap.exists()) {
            const allAlerts = alertsSnap.val();
            const title = form.title.trim().toLowerCase();
            const desc = form.description.trim().toLowerCase();
            for (const [uid, userAlerts] of Object.entries(allAlerts)) {
              if (uid === currentUser.uid) continue;
              for (const alert of Object.values(userAlerts)) {
                const keywordMatch = !alert.keyword || title.includes(alert.keyword.toLowerCase()) || desc.includes(alert.keyword.toLowerCase());
                const categoryMatch = !alert.category || alert.category === form.category;
                const collegeMatch = !alert.college || alert.college === (userProfile?.college || "");
                if (keywordMatch && categoryMatch && collegeMatch) {
                  await push(dbRef(db, `notifications/${uid}`), {
                    type: "alert_match",
                    message: `New listing matches your alert: "${form.title.trim()}" for ₹${Number(form.price) || 0}`,
                    listingId: newRef.key,
                    createdAt: Date.now(),
                    read: false,
                  });
                  break;
                }
              }
            }
          }
        } catch (e) {}

        // Auto-match open Requests with this new listing
        try {
          const reqSnap = await get(dbRef(db, "requests"));
          if (reqSnap.exists()) {
            const titleWords = form.title.trim().toLowerCase().split(/\s+/).filter(w => w.length > 3);
            const notified = new Set();
            Object.entries(reqSnap.val()).forEach(async ([reqId, req]) => {
              if (!req.userId || req.userId === currentUser.uid) return;
              if (notified.has(req.userId)) return;
              const rTitle = (req.itemName || req.title || "").toLowerCase();
              const catMatch = req.category && req.category === form.category;
              const wordMatch = titleWords.some(w => rTitle.includes(w));
              if (catMatch || wordMatch) {
                notified.add(req.userId);
                await push(dbRef(db, `notifications/${req.userId}`), {
                  type: "alert_match",
                  message: `🔔 "${form.title.trim()}" just listed — matches your request! Tap to view.`,
                  listingId: newRef.key,
                  createdAt: Date.now(),
                  read: false,
                });
              }
            });
          }
        } catch (e) { /* silent */ }

        navigate("/my-listings");
      }
    } catch (err) {
      
      setError("Failed to save listing. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  const qualityScore = (() => {
    let score = 0;
    const tips = [];
    if (images.length > 0) score += 30; else tips.push("Add at least 1 photo (+30%)");
    if (images.length >= 3) score += 10; else if (images.length > 0) tips.push("Add 3+ photos for better visibility (+10%)");
    if (form.title.trim().length >= 10) score += 15; else tips.push("Write a descriptive title (+15%)");
    if (form.description.trim().length >= 20) score += 20; else tips.push("Add a description (+20%)");
    if (form.price && Number(form.price) > 0) score += 15; else tips.push("Set a specific price (+15%)");
    if (form.category) score += 5; // category (required anyway)
    if (form.condition) score += 5; // condition (required anyway)
    return { score: Math.min(score, 100), tips };
  })();

  if (loadingEdit) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="pb-24 bg-gray-50 min-h-screen">
      <div className="bg-white px-4 py-4 border-b sticky top-14 z-10 flex items-center gap-3">
        {editId && (
          <button onClick={() => navigate(-1)} className="p-1.5 -ml-1 rounded-full hover:bg-gray-100">
            <ArrowLeft size={20} className="text-gray-700" />
          </button>
        )}
        <div>
          <h2 className="text-lg font-bold text-gray-800">{editId ? "Edit Listing" : "Post a Listing"}</h2>
          <p className="text-xs text-gray-400">{editId ? "Update your listing details" : "Fill in details to sell your item"}</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="px-4 pt-4 space-y-4">
        {error && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-3 text-sm">
            <AlertCircle size={16} className="flex-shrink-0" />
            {error}
          </div>
        )}

        {!editId && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
            <p className="text-sm font-bold text-amber-800 mb-1">⚠️ Fraud Warning — Read Before Posting</p>
            <ul className="text-xs text-amber-700 space-y-1 list-disc list-inside">
              <li>Only list items you actually own and intend to sell.</li>
              <li>Do NOT post fake, duplicate, or misleading listings.</li>
              <li>Never ask buyers to pay in advance outside the platform.</li>
              <li>Fraud listings are reported by the community and auto-removed.</li>
              <li>Accounts with 5+ reports are automatically suspended.</li>
            </ul>
          </div>
        )}

        {!editId && (
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-3">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-gray-700">Listing Quality</p>
              <span className={`text-xs font-bold ${qualityScore.score >= 70 ? "text-green-600" : qualityScore.score >= 40 ? "text-amber-600" : "text-red-500"}`}>
                {qualityScore.score}% {qualityScore.score >= 70 ? "🌟" : qualityScore.score >= 40 ? "📈" : "📉"}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2 mb-2">
              <div className={`h-2 rounded-full transition-all ${qualityScore.score >= 70 ? "bg-green-500" : qualityScore.score >= 40 ? "bg-amber-500" : "bg-red-500"}`}
                style={{ width: `${qualityScore.score}%` }} />
            </div>
            {qualityScore.tips.length > 0 && (
              <p className="text-xs text-gray-500">{qualityScore.tips[0]}</p>
            )}
          </div>
        )}

        {/* Multi-image Upload */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-gray-700">
              Photos {images.length > 0 && <span className="text-gray-400">({images.length}/{MAX_IMAGES})</span>}
            </label>
            {images.length > 0 && <span className="text-xs text-gray-400">First photo is the main image</span>}
          </div>
          <div className="flex gap-2 overflow-x-auto pb-1">
            {images.map((img, idx) => (
              <div key={idx} className="relative flex-shrink-0 w-24 h-24 rounded-xl overflow-hidden border-2 border-gray-200">
                <img
                  src={img.type === "existing" ? img.url : img.preview}
                  alt=""
                  className="w-full h-full object-cover"
                />
                {idx === 0 && (
                  <span className="absolute bottom-0 left-0 right-0 bg-blue-600/80 text-white text-[10px] font-bold text-center py-0.5">
                    Main
                  </span>
                )}
                <button
                  type="button"
                  onClick={() => removeImage(idx)}
                  className="absolute top-1 right-1 bg-black/60 text-white rounded-full w-5 h-5 flex items-center justify-center"
                >
                  <X size={10} />
                </button>
              </div>
            ))}
            {images.length < MAX_IMAGES && (
              <button
                type="button"
                onClick={() => setShowImagePicker(true)}
                className="flex-shrink-0 w-24 h-24 rounded-xl border-2 border-dashed border-blue-300 flex flex-col items-center justify-center gap-1 text-blue-500 hover:bg-blue-50"
              >
                <Plus size={22} />
                <span className="text-xs font-medium">Add Photo</span>
              </button>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={e => handleImageFiles(e.target.files)}
            className="hidden"
          />
          {images.length === 0 && (
            <button
              type="button"
              onClick={() => setShowImagePicker(true)}
              className="w-full border-2 border-dashed border-blue-300 rounded-xl h-24 flex flex-col items-center justify-center gap-2 text-blue-500 hover:bg-blue-50 mt-2"
            >
              <Camera size={24} />
              <span className="text-xs font-medium">Add Photos (Camera or Gallery)</span>
            </button>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
          <input
            name="title"
            value={form.title}
            onChange={handleChange}
            required
            placeholder="What are you selling? (e.g. MTech Reference Books)"
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>

        {form.category === "Books" || !form.category ? (
          <div className="flex flex-col gap-1.5 -mt-2">
            <div className="flex items-center gap-2">
              <button type="button" onClick={scanBarcode} disabled={scanning}
                className="flex items-center gap-1.5 text-xs text-blue-600 font-semibold bg-blue-50 px-3 py-1.5 rounded-lg border border-blue-100 hover:bg-blue-100 disabled:opacity-50">
                {scanning ? <span className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin inline-block" /> : "📷"} {scanning ? "Scanning..." : "Scan Book Barcode"}
              </button>
              <span className="text-xs text-gray-400">Auto-fill from ISBN</span>
            </div>
            {scanStatus && <p className="text-xs font-medium text-blue-700 bg-blue-50 px-3 py-1.5 rounded-lg">{scanStatus}</p>}
          </div>
        ) : null}

        {dupWarning && (
          <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-700 rounded-xl px-3 py-2 text-xs -mt-2">
            ⚠️ You already have a similar active listing. Make sure this is not a duplicate.
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Category *</label>
            <select
              name="category"
              value={form.category}
              onChange={handleChange}
              required
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-700"
            >
              <option value="">Select Category</option>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Condition *</label>
            <select
              name="condition"
              value={form.condition}
              onChange={handleChange}
              required
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent text-gray-700"
            >
              <option value="">Select Condition</option>
              {CONDITIONS.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>

        {!isRental && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Price *</label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">₹</span>
              <input
                name="price"
                type="number"
                min="0"
                value={form.price}
                onChange={handleChange}
                required={!isRental}
                placeholder="0 = Negotiable"
                className="w-full border border-gray-200 rounded-xl pl-8 pr-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <p className="text-xs text-gray-400 mt-1">Enter 0 if price is negotiable</p>
            {priceSuggestion && (
              <div className="flex items-center gap-2 bg-blue-50 border border-blue-100 rounded-xl px-3 py-2 text-xs text-blue-700 mt-1">
                💡 Similar {form.category} items sold for <strong>₹{priceSuggestion.low}–₹{priceSuggestion.high}</strong> ({priceSuggestion.count} sold)
              </div>
            )}
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
          <textarea
            name="description"
            value={form.description}
            onChange={handleChange}
            rows={3}
            placeholder="Describe your item — condition details, reason for selling, etc."
            className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Your WhatsApp Number *</label>
          <div className="relative">
            <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium">+91</span>
            <input
              name="whatsapp"
              type="tel"
              value={form.whatsapp}
              onChange={handleChange}
              required
              maxLength={10}
              placeholder="Enter your own 10-digit number"
              className="w-full border border-gray-200 rounded-xl pl-12 pr-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <p className="text-xs text-green-600 mt-1 font-medium">✅ Buyers will contact YOU directly on this WhatsApp</p>
          <p className="text-xs text-red-500 mt-0.5">⚠️ Enter your own mobile number — not JustDial or any other number</p>
        </div>

        <label className="flex items-center gap-3 bg-orange-50 border border-orange-200 rounded-xl px-4 py-3 cursor-pointer hover:bg-orange-100 transition">
          <input
            type="checkbox"
            checked={isUrgent}
            onChange={e => setIsUrgent(e.target.checked)}
            className="w-4 h-4 accent-orange-500"
          />
          <div className="flex items-center gap-2">
            <Flame size={16} className="text-orange-500" />
            <div>
              <p className="text-sm font-semibold text-gray-800">Mark as Urgent Sale</p>
              <p className="text-xs text-gray-500">Shows in the Urgent Sales section on Home</p>
            </div>
          </div>
        </label>

        {/* Bundle toggle */}
        <div className="flex items-center justify-between bg-purple-50 border border-purple-100 rounded-xl px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-purple-800">📦 Bundle Deal</p>
            <p className="text-xs text-purple-600">Sell multiple items together at a discount</p>
          </div>
          <button type="button" onClick={() => setIsBundle(b => !b)}
            className={`w-12 h-6 rounded-full transition-all ${isBundle ? "bg-purple-500" : "bg-gray-300"} relative`}>
            <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${isBundle ? "left-6" : "left-0.5"}`} />
          </button>
        </div>
        {isBundle && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">What's in the bundle? *</label>
            <textarea
              value={bundleItems}
              onChange={e => setBundleItems(e.target.value)}
              placeholder="List all items included, e.g.:&#10;• Engineering Maths textbook&#10;• Physics lab manual&#10;• Drawing kit"
              rows={3}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
            />
          </div>
        )}

        {/* Rental toggle */}
        <div className="flex items-center justify-between bg-teal-50 border border-teal-100 rounded-xl px-4 py-3">
          <div>
            <p className="text-sm font-semibold text-teal-800">🏠 Rental Listing</p>
            <p className="text-xs text-teal-600">Offer item on rent — no price field needed above</p>
          </div>
          <button type="button" onClick={() => setIsRental(r => !r)}
            className={`w-12 h-6 rounded-full transition-all ${isRental ? "bg-teal-500" : "bg-gray-300"} relative flex-shrink-0`}>
            <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${isRental ? "left-6" : "left-0.5"}`} />
          </button>
        </div>
        {isRental && (
          <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 space-y-5">
            <p className="text-sm font-bold text-teal-800">🏠 Rental Settings</p>

            {/* ── Pricing ── */}
            <div>
              <p className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Pricing <span className="text-red-500">*</span> <span className="text-gray-400 normal-case font-normal">(set at least one)</span></p>
              <div className="space-y-2">
                {[
                  { enabledKey: "pricePerDayEnabled", priceKey: "pricePerDay", label: "Per Day (₹)" },
                  { enabledKey: "pricePerWeekEnabled", priceKey: "pricePerWeek", label: "Per Week (₹)" },
                  { enabledKey: "pricePerMonthEnabled", priceKey: "pricePerMonth", label: "Per Month (₹)" },
                ].map(({ enabledKey, priceKey, label }) => (
                  <div key={priceKey} className="flex items-center gap-3">
                    <input type="checkbox" className="w-4 h-4 accent-teal-600"
                      checked={rentalData[enabledKey]}
                      onChange={e => setRentalData(prev => ({ ...prev, [enabledKey]: e.target.checked, [priceKey]: e.target.checked ? prev[priceKey] : "" }))} />
                    <span className="text-sm text-gray-600 w-28 flex-shrink-0">{label}</span>
                    {rentalData[enabledKey] && (
                      <div className="relative flex-1">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">₹</span>
                        <input type="number" min="1" value={rentalData[priceKey]}
                          onChange={e => setRD(priceKey, e.target.value)}
                          placeholder="Amount"
                          className="w-full pl-7 pr-3 py-2 border border-teal-300 rounded-xl text-sm focus:ring-2 focus:ring-teal-400 focus:border-transparent bg-white" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* ── Duration limits ── */}
            <div>
              <p className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Rental Duration</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Min days</label>
                  <input type="number" min="1" value={rentalData.minDuration}
                    onChange={e => setRD("minDuration", e.target.value)}
                    className="w-full border border-teal-300 rounded-xl px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-teal-400" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Max days</label>
                  <input type="number" min="1" value={rentalData.maxDuration}
                    onChange={e => setRD("maxDuration", e.target.value)}
                    className="w-full border border-teal-300 rounded-xl px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-teal-400" />
                </div>
              </div>
            </div>

            {/* ── Availability ── */}
            <div>
              <p className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Availability Window</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Available from</label>
                  <input type="date" value={rentalData.availableFrom}
                    min={new Date().toISOString().split("T")[0]}
                    onChange={e => setRD("availableFrom", e.target.value)}
                    className="w-full border border-teal-300 rounded-xl px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-teal-400" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Available to</label>
                  <input type="date" value={rentalData.availableTo}
                    min={rentalData.availableFrom || new Date().toISOString().split("T")[0]}
                    onChange={e => setRD("availableTo", e.target.value)}
                    className="w-full border border-teal-300 rounded-xl px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-teal-400" />
                </div>
              </div>
            </div>

            {/* ── Security deposit ── */}
            <div>
              <p className="text-xs font-semibold text-gray-700 mb-1 uppercase tracking-wide">Security Deposit (optional)</p>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">₹</span>
                <input type="number" min="0" value={rentalData.deposit}
                  onChange={e => setRD("deposit", e.target.value)}
                  placeholder="0 = No deposit"
                  className="w-full pl-7 pr-3 py-2 border border-teal-300 rounded-xl text-sm bg-white focus:ring-2 focus:ring-teal-400" />
              </div>
              <p className="text-xs text-teal-600 mt-1">Refundable on safe return of item</p>
            </div>

            {/* ── Delivery ── */}
            <div>
              <p className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Pickup / Delivery</p>
              <div className="flex gap-2">
                {[{ v: "pickup", l: "📍 Pickup only" }, { v: "delivery", l: "🚚 Delivery only" }, { v: "both", l: "🔄 Both" }].map(({ v, l }) => (
                  <button key={v} type="button" onClick={() => setRD("deliveryType", v)}
                    className={`flex-1 py-2 rounded-xl text-xs font-semibold border-2 transition ${rentalData.deliveryType === v ? "border-teal-500 bg-teal-100 text-teal-800" : "border-gray-200 bg-white text-gray-500"}`}>
                    {l}
                  </button>
                ))}
              </div>
              {rentalData.deliveryType !== "pickup" && (
                <div className="relative mt-2">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">₹</span>
                  <input type="number" min="0" value={rentalData.deliveryCharge}
                    onChange={e => setRD("deliveryCharge", e.target.value)}
                    placeholder="Delivery charge (0 = free)"
                    className="w-full pl-7 pr-3 py-2 border border-teal-300 rounded-xl text-sm bg-white focus:ring-2 focus:ring-teal-400 mt-0" />
                </div>
              )}
            </div>

            {/* ── Rental Rules ── */}
            <div>
              <p className="text-xs font-semibold text-gray-700 mb-2 uppercase tracking-wide">Rental Rules</p>
              <div className="space-y-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Cancellation policy</label>
                  <div className="flex gap-2">
                    {[{ v: "free", l: "✅ Free cancel" }, { v: "partial", l: "⚡ Partial refund" }, { v: "none", l: "❌ No refund" }].map(({ v, l }) => (
                      <button key={v} type="button" onClick={() => setRD("cancellationPolicy", v)}
                        className={`flex-1 py-2 rounded-xl text-xs font-semibold border-2 transition ${rentalData.cancellationPolicy === v ? "border-teal-500 bg-teal-100 text-teal-800" : "border-gray-200 bg-white text-gray-500"}`}>
                        {l}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Late return fee (₹ per extra day)</label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">₹</span>
                    <input type="number" min="0" value={rentalData.lateFeePerDay}
                      onChange={e => setRD("lateFeePerDay", e.target.value)}
                      placeholder="0 = No late fee"
                      className="w-full pl-7 pr-3 py-2 border border-teal-300 rounded-xl text-sm bg-white focus:ring-2 focus:ring-teal-400" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {!editId && (
          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={agreedToGuidelines}
              onChange={e => setAgreedToGuidelines(e.target.checked)}
              className="w-4 h-4 mt-0.5 accent-blue-600 flex-shrink-0"
            />
            <p className="text-xs text-gray-600">
              I confirm this listing is genuine. I understand that fake or fraudulent listings will be removed by the community and my account may be suspended.
            </p>
          </label>
        )}

        <button
          type="submit"
          disabled={loading || (!editId && !agreedToGuidelines)}
          className="w-full bg-blue-600 text-white py-3.5 rounded-xl font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-base"
        >
          {loading ? (editId ? "Saving..." : "Posting...") : (editId ? "Save Changes" : "Post Listing")}
        </button>
      </form>

      {/* Image Picker Bottom Sheet */}
      {showImagePicker && (
        <div
          style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 9999 }}
          onClick={() => setShowImagePicker(false)}
        >
          <div
            style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "#fff", borderRadius: "20px 20px 0 0", padding: "24px", maxWidth: "512px", margin: "0 auto" }}
            onClick={e => e.stopPropagation()}
          >
            <div className="w-10 h-1 bg-gray-200 rounded-full mx-auto mb-5" />
            <p className="text-base font-bold text-gray-800 mb-5 text-center">Add Photo</p>
            <div className="grid grid-cols-2 gap-3 mb-4">
              <button
                type="button"
                onClick={() => {
                  setShowImagePicker(false);
                  const inp = document.createElement("input");
                  inp.type = "file"; inp.accept = "image/*"; inp.capture = "environment";
                  inp.onchange = e => handleImageFiles(e.target.files);
                  inp.click();
                }}
                className="flex flex-col items-center justify-center gap-3 bg-blue-50 border border-blue-100 rounded-2xl py-6 text-blue-600 hover:bg-blue-100 transition"
              >
                <Camera size={28} />
                <div className="text-center">
                  <p className="text-sm font-semibold">Camera</p>
                  <p className="text-xs text-blue-400 mt-0.5">Take a new photo</p>
                </div>
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowImagePicker(false);
                  setTimeout(() => fileInputRef.current?.click(), 100);
                }}
                className="flex flex-col items-center justify-center gap-3 bg-gray-50 border border-gray-200 rounded-2xl py-6 text-gray-600 hover:bg-gray-100 transition"
              >
                <span className="text-3xl leading-none">🖼️</span>
                <div className="text-center">
                  <p className="text-sm font-semibold">Gallery</p>
                  <p className="text-xs text-gray-400 mt-0.5">Choose from photos</p>
                </div>
              </button>
            </div>
            <button
              type="button"
              onClick={() => setShowImagePicker(false)}
              className="w-full py-3 text-sm font-semibold text-gray-500 bg-gray-100 rounded-xl hover:bg-gray-200 transition"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
